"use client"

import type * as React from "react"
import { Users, Building2, ClipboardList, Mail, Target, Link, Plus, PanelLeft, CreditCard, UserCog, History, Activity, IdCard } from "lucide-react"
import Image from "next/image"
import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { TeamSwitcher } from "@/components/team-switcher"
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail, SidebarMenu, SidebarMenuItem, SidebarMenuButton, useSidebar } from "@/components/ui/sidebar"
import { DashboardIcon } from "@/components/dashboard-icon"

// This is sample data.
const data = {
  user: {
    name: "Link Builder",
    email: "user@zipsprout.com",
  },
  teams: [
    {
      name: "Zipsprout",
      logo: ({ className }: { className?: string } = {}) => (
        <Image
          src="/zipsprout-icon.png"
          alt="Zipsprout"
          width={16}
          height={16}
          className={className || "size-4"}
          style={{
            width: "auto",
            height: "100%",
            objectFit: "contain",
            mixBlendMode: "screen",
          }}
        />
      ),
      plan: "Enterprise",
    },
    {
      name: "Link Building Team",
      logo: Users,
      plan: "Pro",
    },
    {
      name: "SEO Campaign",
      logo: Mail,
      plan: "Free",
    },
  ],
  navMain: [
    {
      title: "Dashboard",
      url: "/",
      icon: DashboardIcon,
      isActive: true,
      // RELEASE_HIDDEN: Re-enabled for ROCO-501 Dashboard Page prep
    },
    {
      title: "Clients",
      url: "/clients",
      icon: Users,
      items: [
        {
          title: "Businesses",
          url: "/businesses",
          icon: Building2,
        },
      ],
    },
    {
      title: "Organizations",
      url: "/organizations",
      icon: Building2,
      // RELEASE_HIDDEN: Organizations re-enabled for ROCO-433
      items: [
        {
          title: "Opportunities",
          url: "/opportunities",
          icon: Target,
          // RELEASE_HIDDEN: Opportunities subitem re-enabled for ROCO-447
          // ROCO-447: Opportunity Page prod prep - Opportunities subitem enabled
        },
        {
          title: "Membership Information",
          url: "/opportunities/membership-information",
          icon: IdCard,
        },
      ],
    },
    {
      title: "Orders",
      url: "/orders",
      icon: ClipboardList,
      // RELEASE_HIDDEN: Orders re-enabled for ROCO-483
      items: [
        {
          title: "Create Client Order",
          url: "/orders/create-for-client",
          icon: Plus,
          // RELEASE_HIDDEN: Re-enabled for ROCO-507 Create Order Page prep
        },
      ],
    },
    {
      title: "Order Tracking",
      // Parent group: clicking only expands/collapses (no page of its own).
      url: "#",
      icon: Activity,
      isGroup: true,
      items: [
        {
          title: "Outreach Tracking",
          url: "/outreach-tracking",
          icon: History,
        },
        {
          title: "Sponsorship Payment",
          url: "/sponsorship-payment",
          icon: CreditCard,
        },
        {
          title: "Link Checker",
          url: "/link-checker",
          icon: Link,
        },
      ],
    },
    {
      title: "User Management",
      url: "/settings/users",
      icon: UserCog,
    },
    // {
    //   title: "Tasks",
    //   url: "/tasks",
    //   icon: Calendar,
    //   items: [
    //     {
    //       title: "All Tasks",
    //       url: "/tasks",
    //     },
    //     {
    //       title: "Pending Validation",
    //       url: "/tasks/pending-validation",
    //     },
    //     {
    //       title: "Due Today",
    //       url: "/tasks/today",
    //     },
    //   ],
    // },
    // {
    //   title: "Email Workflows",
    //   url: "/email-workflows",
    //   icon: Mail,
    //   items: [
    //     {
    //       title: "Active Workflows",
    //       url: "/email-workflows",
    //     },
    //     {
    //       title: "Templates",
    //       url: "/email-workflows/templates",
    //     },
    //     {
    //       title: "Analytics",
    //       url: "/email-workflows/analytics",
    //     },
    //   ],
    // },
    // {
    //   title: "Validation",
    //   url: "/validation",
    //   icon: Settings2,
    //   items: [
    //     {
    //       title: "Criteria",
    //       url: "/validation/criteria",
    //     },
    //     {
    //       title: "Rules",
    //       url: "/validation/rules",
    //     },
    //     {
    //       title: "History",
    //       url: "/validation/history",
    //     },
    //   ],
    // },
  ],
  // projects: [
  //   {
  //     name: "Q1 Campaign",
  //     url: "#",
  //     icon: Frame,
  //   },
  //   {
  //     name: "Link Building",
  //     url: "#",
  //     icon: PieChart,
  //   },
  //   {
  //     name: "Content Marketing",
  //     url: "#",
  //     icon: Map,
  //   },
  // ],
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  // Use navigation items directly - consistent across all environments
  const filteredNavItems = data.navMain
  const { toggleSidebar, state } = useSidebar()
  const isCollapsed = state === "collapsed"

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={data.teams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={filteredNavItems} />
        {/* <NavProjects projects={data.projects} /> */}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton tooltip={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"} onClick={toggleSidebar} className="hover:text-[#2563eb]">
              <PanelLeft />
              <span>{isCollapsed ? "Expand" : "Collapse"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <NavUser user={data.user} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
